import {
  Content,
  ContentHeader,
  Header,
  InfoCard,
  Link,
  Page,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/core-plugin-api';
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

type ProductRepoPointer = {
  name: string;
  title: string;
  description: string;
  sourceLocation: string;
  githubSlug: string;
  registryRepoId: string;
  releaseNamespace: string;
  deployWorkflow: string;
  deliveryMaturity: string;
  proofLevel: string;
  blockingGaps: string[];
};

type ReleaseEvidenceSummary = {
  deployment: {
    branch: string;
    commit: string;
    image: string;
    workflowRunUrl: string;
    deployedAt: string;
  };
  validators: {
    status: string;
    commands: Record<string, string>;
  };
  browserSmoke: {
    status: string;
    command: string;
    evidenceUrl: string;
  };
  productRepoPointers: ProductRepoPointer[];
};

type ReleaseEvidenceState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; summary: ReleaseEvidenceSummary };

const useStyles = makeStyles(theme => ({
  hero: {
    padding: theme.spacing(4),
    borderRadius: theme.shape.borderRadius * 3,
    background:
      'linear-gradient(135deg, rgba(18,45,40,0.96), rgba(67,87,55,0.9))',
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.common.white,
    marginBottom: theme.spacing(4),
  },
  metricGrid: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    marginTop: theme.spacing(3),
  },
  metricCard: {
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 2,
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.12)',
  },
  loading: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(2),
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  commandList: {
    display: 'grid',
    gap: theme.spacing(1),
  },
  commandItem: {
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
  },
}));

async function fetchReleaseEvidence(
  discoveryApi: DiscoveryApi,
  fetchApi: FetchApi,
): Promise<ReleaseEvidenceSummary> {
  const baseUrl = await discoveryApi.getBaseUrl('c4ci-release-evidence');
  const response = await fetchApi.fetch(`${baseUrl}/v1/summary`);
  if (!response.ok) {
    throw new Error(`${response.status} ${response.statusText}`.trim());
  }
  return (await response.json()) as ReleaseEvidenceSummary;
}

function githubUrl(sourceLocation: string): string {
  return sourceLocation.replace(/^url:/, '');
}

export const FactoryReleaseEvidencePage = () => {
  const classes = useStyles();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [state, setState] = useState<ReleaseEvidenceState>({
    status: 'loading',
  });

  useEffect(() => {
    let cancelled = false;

    fetchReleaseEvidence(discoveryApi, fetchApi)
      .then(summary => {
        if (!cancelled) {
          setState({ status: 'ready', summary });
        }
      })
      .catch(error => {
        if (!cancelled) {
          setState({
            status: 'error',
            message:
              error instanceof Error
                ? error.message
                : 'Unable to load release evidence',
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi]);

  return (
    <Page themeId="home">
      <Header
        title="Release Evidence"
        subtitle="Live deploy, validator, browser-smoke, and product-pointer proof"
      />
      <Content>
        <Box className={classes.hero}>
          <Typography variant="h4" component="h2" gutterBottom>
            Factory release truth, pulled from the running backend.
          </Typography>
          <Typography variant="body1">
            This page does not use fixture cards. It reads the deployed runtime
            evidence endpoint, generated catalog projections, and release
            workflow metadata exported by the dev deployment.
          </Typography>
          {state.status === 'ready' ? (
            <div className={classes.metricGrid}>
              <div className={classes.metricCard}>
                <Typography variant="caption">Deployed branch</Typography>
                <Typography variant="h5">
                  {state.summary.deployment.branch || 'not exported'}
                </Typography>
              </div>
              <div className={classes.metricCard}>
                <Typography variant="caption">Deployed commit</Typography>
                <Typography variant="h5">
                  {state.summary.deployment.commit
                    ? state.summary.deployment.commit.slice(0, 12)
                    : 'not exported'}
                </Typography>
              </div>
              <div className={classes.metricCard}>
                <Typography variant="caption">Product pointers</Typography>
                <Typography variant="h5">
                  {state.summary.productRepoPointers.length}
                </Typography>
              </div>
            </div>
          ) : null}
        </Box>

        {state.status === 'loading' ? (
          <InfoCard title="Loading release evidence">
            <Box className={classes.loading}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="textSecondary">
                Resolving the live release evidence backend endpoint.
              </Typography>
            </Box>
          </InfoCard>
        ) : null}

        {state.status === 'error' ? (
          <InfoCard title="Release evidence unavailable">
            <Typography variant="body2" color="error">
              {state.message}
            </Typography>
          </InfoCard>
        ) : null}

        {state.status === 'ready' ? (
          <>
            <ContentHeader title="Deployment Proof" />
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <InfoCard title="Runtime Image">
                  <Typography variant="body2" color="textSecondary" paragraph>
                    {state.summary.deployment.image || 'No image exported by this local runtime.'}
                  </Typography>
                  <Typography variant="body2">
                    Deployed at:{' '}
                    {state.summary.deployment.deployedAt || 'not exported'}
                  </Typography>
                  {state.summary.deployment.workflowRunUrl ? (
                    <Link to={state.summary.deployment.workflowRunUrl}>
                      Open deploy workflow evidence
                    </Link>
                  ) : null}
                </InfoCard>
              </Grid>
              <Grid item xs={12} md={6}>
                <InfoCard title="Browser Smoke">
                  <Typography variant="body2" paragraph>
                    Status: {state.summary.browserSmoke.status}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Command: {state.summary.browserSmoke.command}
                  </Typography>
                </InfoCard>
              </Grid>
            </Grid>

            <Box mt={4}>
              <ContentHeader title="Validator Gate" />
              <InfoCard title={`Validator status: ${state.summary.validators.status}`}>
                <div className={classes.commandList}>
                  {Object.entries(state.summary.validators.commands).map(
                    ([key, command]) => (
                      <div className={classes.commandItem} key={key}>
                        <Typography variant="body2">
                          <strong>{key}</strong>
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {command}
                        </Typography>
                      </div>
                    ),
                  )}
                </div>
              </InfoCard>
            </Box>

            <Box mt={4}>
              <ContentHeader title="Product Repo Pointers">
                <VerifiedUserIcon color="primary" />
              </ContentHeader>
              <Grid container spacing={3}>
                {state.summary.productRepoPointers.map(pointer => (
                  <Grid item xs={12} md={4} key={pointer.name}>
                    <InfoCard
                      title={pointer.title || pointer.name}
                      subheader={pointer.releaseNamespace}
                    >
                      <Typography variant="body2" color="textSecondary" paragraph>
                        {pointer.description}
                      </Typography>
                      <Typography variant="body2">
                        Workflow: {pointer.deployWorkflow || 'not declared'}
                      </Typography>
                      <Typography variant="body2">
                        Proof: {pointer.proofLevel || 'not declared'}
                      </Typography>
                      <div className={classes.chipRow}>
                        <Chip
                          label={pointer.deliveryMaturity || 'unknown maturity'}
                          size="small"
                        />
                        {pointer.blockingGaps.map(gap => (
                          <Chip key={gap} label={gap} size="small" />
                        ))}
                      </div>
                      <Box mt={2}>
                        <Link
                          component={RouterLink}
                          to={`/catalog/default/component/${pointer.name}`}
                        >
                          Open catalog entity
                        </Link>
                        {pointer.sourceLocation ? (
                          <>
                            {' · '}
                            <Link to={githubUrl(pointer.sourceLocation)}>
                              Open source
                            </Link>
                          </>
                        ) : null}
                      </Box>
                    </InfoCard>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </>
        ) : null}
      </Content>
    </Page>
  );
};
